import Css from "./style.module.scss";

import * as Sentry from "@sentry/browser";
import { PushNotifications } from "@capacitor/push-notifications";
import { SplashScreen } from "@capacitor/splash-screen";
import { ToastContainer } from "react-toastify";
import { checkAuthCallbackState, getUserData } from "selectors/user";
import { checkPreloaderOverlayShown, checkPreloaderShown, getModalWindowConfig } from "selectors/ui";
import { getActiveOrganization } from "selectors/organizations";
import { getEnvVars } from "selectors/envVars";
import { getSelectedBusinessData, getSelectedBusinessId, getUserBusinessesData } from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import AccountsActions from "actions/AccountsActions";
import Async from "utils/Async";
import AuthZeroActions from "actions/AuthZeroActions";
import BrowserEvents from "const/BrowserEvents";
import Businesses from "utils/Businesses";
import BusinessesActions from "actions/BusinessesActions";
import Constants from "const/Constants";
import ContactsActions from "actions/ContactsActions";
import Countries from "const/Countries";
import Environment from "utils/Environment";
import Errors from "const/Errors";
import InstitutionsActions from "actions/InstitutionsActions";
import Intercom from "utils/Intercom";
import IntercomActions from "actions/IntercomActions";
import LocalStorageKeys from "const/LocalStorageKeys";
import LocationHistoryContext from "contexts/LocationHistoryContext";
import MainApiActions from "actions/MainApiActions";
import MaintenanceHandler from "nlib/common/MaintenanceHandler";
import MetaDataActions from "actions/MetaDataActions";
import ModalContainer from "nlib/common/ModalContainer";
import OpenReplay from "utils/OpenReplay";
import OrganizationsActions from "actions/OrganizationsActions";
import Platforms from "utils/Platforms";
import PreAccountingActions from "actions/PreAccountingActions";
import Preloader from "nlib/common/Preloader";
import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import Storage from "utils/Storage";
import StripeActions from "actions/StripeActions";
import TasksActions from "actions/TasksActions";
import TextsActions from "actions/TextsActions";
import UiActions from "actions/UiActions";
import UiRoutes from "const/UiRoutes";
import Urls from "utils/Urls";
import UserActions from "actions/UserActions";
import UserRoles from "const/UserRoles";
import VatClassificationActions from "actions/VatClassificationActions";

const OldApp = lazy(() => import("lib/App"));

const NewApp = lazy(() => import("nlib/App"));

const MobileApp = lazy(() => import("mlib/App"));

let initialViewportValue = null;

const toggleMobileViewportCompatibilityMode = () => {
  const portraitOrientation = window.innerHeight > window.innerWidth;

  const screenWidth = portraitOrientation || screen.width > screen.height ? window.screen.width : window.screen.height;

  const viewportMeta = document.querySelector("meta[name='viewport']");

  if (!initialViewportValue) {
    initialViewportValue = viewportMeta.getAttribute("content");
  }
  if (screenWidth <= Constants.LAYOUT_MIN_WIDTH) {
    viewportMeta.setAttribute("content", `width=${Constants.LAYOUT_MIN_WIDTH}`);
  } else {
    viewportMeta.setAttribute("content", initialViewportValue);
  }
};

const handleWindowResize = () => {
  toggleMobileViewportCompatibilityMode();
};

const checkBusinessIdValid = (businessId, businesses) => {
  return businessId && businesses.some(({ id }) => id === businessId);
};

const parseReturnTo = (returnTo) => {
  if (!returnTo) return {};

  const [pathname = UiRoutes.MAIN, search = ""] = returnTo.split("?");

  return {
    pathname,
    search: search ? `?${search}` : "",
    envVars: Urls.parseQueryString(search)
  };
};

const STANDALONE_ROUTES = [
  UiRoutes.INTEGRATIONS,
  UiRoutes.LOGIN,
  UiRoutes.LOGOUT,
  UiRoutes.SETTINGS,
  UiRoutes.SIGNUP
];

const parseCurrentPath = (currentPathname) => {
  const [firstPathPart = "", secondPathPart = ""] = currentPathname.split("/").filter(Boolean);

  if (!firstPathPart) {
    return { businessId: "", route: UiRoutes.MAIN };
  }
  if (STANDALONE_ROUTES.includes(`/${firstPathPart}`)) {
    return { businessId: "", route: `/${firstPathPart}` };
  }

  return {
    businessId: firstPathPart,
    route: secondPathPart ? `/${secondPathPart}` : UiRoutes.MAIN
  };
};

const App = ({ onAppInitComplete }) => {
  const history = useHistory();

  const locationHistoryRef = useRef([]);

  const { params: { businessId: paramsBusinessId } } = useRouteMatch("/:businessId?");

  const { pathname, search } = useLocation();

  const dispatch = useDispatch();

  const envVars = useSelector(getEnvVars);

  const authZeroCallbackState = useSelector(checkAuthCallbackState);

  const preloaderShown = useSelector(checkPreloaderShown);

  const preloaderOverlayShown = useSelector(checkPreloaderOverlayShown);

  const modalWindowConfig = useSelector(getModalWindowConfig);

  const activeOrganization = useSelector(getActiveOrganization);

  const userData = useSelector(getUserData);

  const userBusinessesData = useSelector(getUserBusinessesData);

  const selectedBusinessId = useSelector(getSelectedBusinessId);

  const selectedBusinessData = useSelector(getSelectedBusinessData);

  const intervalsRef = useRef([]);

  const [maintenanceShown, setMaintenanceShown] = useState(false);

  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  const selectedBusinessIdValid = !!checkBusinessIdValid(paramsBusinessId, userBusinessesData);

  const businessUser = UserRoles.checkIsBusiness(userData.role);

  const selectedBusinessRpaMode = Businesses.checkIsRpaMode(selectedBusinessData.extraData);

  const czCountry = activeOrganization?.countryCode === Countries.CZ;

  const oldUi = czCountry && !businessUser;

  const handlePushNotificationsRegistration = useCallback(async({ value }) => {
    await dispatch(UserActions.editUser({
      pushToken: value,
      backgroundUpdate: true,
      silentUpdate: true
    }));
  }, [dispatch]);

  const handlePushNotificationsActionPerformed = useCallback(({ notification }) => {
    const { data: { navigateTo } = {} } = notification;

    if (navigateTo) window.location.href = navigateTo;
  }, []);

  const selectBusiness = useCallback(async(businessId) => {
    if (businessId === selectedBusinessId) return;
    intervalsRef.current.forEach((intervalId) => clearInterval(intervalId));
    await dispatch(BusinessesActions.selectBusiness(businessId, true));
    if (czCountry) {
      dispatch(InstitutionsActions.fetchInstitutionLinks(true));
      dispatch(InstitutionsActions.fetchInstitutionAccounts(true));
    } else {
      await dispatch(AccountsActions.fetchAccountsList(true));
    }
    dispatch(BusinessesActions.fetchGlobalStats(true));
    dispatch(ContactsActions.fetchContactsList(true));
    dispatch(TasksActions.fetchTasksList(true));
    dispatch(UiActions.togglePreloader(false));
    intervalsRef.current.forEach((intervalId) => clearInterval(intervalId));
    intervalsRef.current = [
      setInterval(
        () => dispatch(BusinessesActions.fetchGlobalStats(false)),
        Constants.GLOBAL_STATS_UPDATE_INTERVAL
      ),
      setInterval(
        () => {
          dispatch(BusinessesActions.fetchBusiness(businessId, true, true));
          dispatch(TasksActions.fetchTasksList(false, true));
          if (!czCountry) {
            dispatch(AccountsActions.fetchAccountsList(false, true));
          }
        },
        Constants.DATA_LIST_UPDATE_INTERVAL
      ),
      setInterval(
        () => {
          dispatch(ContactsActions.fetchContactsList(false, true));
          if (czCountry) {
            dispatch(InstitutionsActions.fetchInstitutionLinks(false, true));
            dispatch(InstitutionsActions.fetchInstitutionAccounts(false, true));
          }
        },
        Constants.CONTACTS_DATA_UPDATE_INTERVAL
      )
    ];
  }, [selectedBusinessId, czCountry, dispatch]);

  const fetchInitialData = useCallback(async() => {
    const { DEFAULT_APP_URL, INTERCOM_APP_ID } = Environment.getProcessEnvVars();

    const intercomEnabled = !!INTERCOM_APP_ID && INTERCOM_APP_ID !== "null"
      && (Environment.checkIsLocalhost() || (!Environment.checkIsDevMode() && !Platforms.checkIsTouchDevice()));

    let currentPathname = pathname;

    let currentSearch = search;

    let currentEnvVars = envVars;

    await dispatch(TextsActions.setAppLanguage(null));
    if (Platforms.checkIsNative()) {
      await PushNotifications.addListener("pushNotificationActionPerformed", handlePushNotificationsActionPerformed);
    }
    if (authZeroCallbackState) {
      const { appState } = await dispatch(AuthZeroActions.handleRedirectCallback()) || {};

      const {
        pathname: returnToPathname, search: returnToSearch, envVars: returnToEnvVars
      } = parseReturnTo(appState?.returnTo ? atob(appState.returnTo) : null);

      currentPathname = returnToPathname || UiRoutes.MAIN;
      currentSearch = returnToSearch || "";
      currentEnvVars = returnToEnvVars || {};
      history.replace(`${currentPathname}${currentSearch}`);
    }

    const token = await dispatch(AuthZeroActions.fetchAuthToken(authZeroCallbackState));

    const { businessId: currentBusinessId, route: currentRoute } = parseCurrentPath(currentPathname);

    if (!token) {
      dispatch(UiActions.togglePreloader(false));

      return null;
    }
    if (currentRoute === UiRoutes.LOGOUT) {
      dispatch(AuthZeroActions.logoutUser());

      return null;
    }
    dispatch(MainApiActions.setAuthToken(token));

    let checkoutSessionFailed = false;

    if (currentEnvVars.checkoutSessionId) {
      checkoutSessionFailed = !(await dispatch(StripeActions.fullfillCheckoutSession(currentEnvVars.checkoutSessionId)));
      delete currentEnvVars.checkoutSessionId;
      currentSearch = Urls.objectToQueryString(currentEnvVars);
      history.replace(`${currentPathname}${currentSearch ? `?${currentSearch}` : ""}`);
    }

    // It's important to make first api call separately, and then call others (in parallel or ordered)
    const organizations = await dispatch(OrganizationsActions.fetchOrganizationsList(false, true));

    const [userInfo, users, businesses] = await Promise.all([
      dispatch(AuthZeroActions.fetchUserInfo(false, true)),
      dispatch(OrganizationsActions.fetchUsersList(false, true)),
      dispatch(BusinessesActions.fetchBusinessesList(true, false, true))
    ]);

    const currentUser = (users || []).find(({ sub }) => sub === userInfo.sub) || {};

    if (!userInfo || !users || !currentUser || !organizations || !businesses) {
      throw Errors.CRITICAL_DATA_NOT_LOADED;
    }

    const currentActiveOrganization = organizations.find(({ active }) => active);

    const czCountryInternal = currentActiveOrganization.countryCode === Countries.CZ;

    try {
      Sentry.setContext("userData", currentUser);
      Sentry.setTag("userEmail", currentUser.email);
    } catch (error) {}
    if (Platforms.checkIsTouchDevice()
      && currentActiveOrganization.countryCode === Countries.CZ
      && !UserRoles.checkIsBusiness(currentUser.role)) {
      const { RESIZE, FULLSCREEN_CHANGE, ORIENTATION_CHANGE } = BrowserEvents;

      window.document.body.dataset.oldUi = "";
      window.document.body.dataset.desktop = "";
      delete window.document.body.dataset.mobile;
      toggleMobileViewportCompatibilityMode();
      window.addEventListener(RESIZE, handleWindowResize);
      window.addEventListener(FULLSCREEN_CHANGE, handleWindowResize);
      window.addEventListener(ORIENTATION_CHANGE, handleWindowResize);
    }
    if (intercomEnabled && !UserRoles.checkIsBusiness(currentUser.role)) {
      const intercomUserJwt = await dispatch(IntercomActions.fetchUserJwt());

      if (intercomUserJwt) {
        Intercom.init({
          app_id: INTERCOM_APP_ID,
          intercom_user_jwt: intercomUserJwt
        });
      }
    }
    if (currentActiveOrganization.temp) {
      setTimeout(() => dispatch(AuthZeroActions.logoutUser()), Constants.AUTO_LOGOUT_FOR_TEMP_USER);
    }
    if (!Environment.checkIsDevMode() && !Environment.checkIsVercelDomain()) {
      const { domainName } = currentActiveOrganization.whiteLabel || {};

      if (domainName && domainName !== window.location.host) {
        window.location.replace(`//${domainName}${window.location.pathname}${window.location.search}`);
        await Async.waitForever();
      } else if (!domainName && !Environment.checkIsDefaultAppDomain()) {
        window.location.replace(`${DEFAULT_APP_URL}${window.location.pathname}${window.location.search}`);
        await Async.waitForever();
      }
    }

    const [currencies, prices, banks, institutions] = await Promise.all([
      dispatch(MetaDataActions.fetchCurrenciesList()),
      dispatch(MetaDataActions.fetchPricesList()),
      ...(czCountryInternal ? [
        dispatch(MetaDataActions.fetchBanksList()),
        dispatch(InstitutionsActions.fetchInstitutionsList())
      ] : [])
    ]);

    if (!currencies || !prices || (czCountryInternal && (!banks || !institutions))) throw Errors.CRITICAL_DATA_NOT_LOADED;
    setInitialDataLoaded(true);
    if (Platforms.checkIsNative() && UserRoles.checkIsBusiness(currentUser.role)) {
      (async() => {
        let pushNotificationsPermissions = false;

        ({ receive: pushNotificationsPermissions } = await PushNotifications.checkPermissions());
        if (pushNotificationsPermissions === "prompt") {
          ({ receive: pushNotificationsPermissions } = await PushNotifications.requestPermissions());
        }
        if (pushNotificationsPermissions === "granted") {
          await PushNotifications.addListener("registration", handlePushNotificationsRegistration);
          await PushNotifications.register();
        }
      })();
    }

    const userBusinessIds = currentUser.businessIds || [];

    const userBusinesses = businesses.filter(({ id }) => !userBusinessIds.length || userBusinessIds.includes(id));

    const singleBusiness = userBusinesses.length === 1;

    const innerBusinessUser = UserRoles.checkIsBusiness(currentUser.role);

    const result = { checkoutSessionFailed, currentActiveOrganization };

    if (!userBusinesses.length || (
      (!Platforms.checkIsTouchDevice() || czCountryInternal) && (
        ((currentRoute === UiRoutes.MAIN) && !innerBusinessUser && !singleBusiness)
        || (currentRoute === UiRoutes.SETTINGS && !innerBusinessUser)
        || (currentRoute === UiRoutes.INTEGRATIONS))
    )) {
      dispatch(UiActions.togglePreloader(false));

      return result;
    }
    if (checkBusinessIdValid(currentBusinessId, userBusinesses)) return true;
    if (!Platforms.checkIsTouchDevice() && !innerBusinessUser && !singleBusiness) {
      history.push(UiRoutes.MAIN);
      dispatch(UiActions.togglePreloader(false));

      return result;
    }

    let fallbackBusinessId = Storage.storageValue(LocalStorageKeys.SELECTED_BUSINESS);

    if (checkBusinessIdValid(fallbackBusinessId, userBusinesses)) {
      history.push(`/${fallbackBusinessId}`);

      return result;
    }
    fallbackBusinessId = (userBusinesses.find(({ id }) => id === currentActiveOrganization.businessId) || userBusinesses[0]).id;
    history.push(`/${fallbackBusinessId}`);

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async() => {
      try {
        const { checkoutSessionFailed, currentActiveOrganization } = await fetchInitialData() || {};

        if (checkoutSessionFailed) {
          await dispatch(async(innerDispatch, getState) => {
            const { errors = {}, uiTexts = {} } = getTextsData(getState()) || {};

            await innerDispatch(UiActions.showModal(
              errors.paymentMethodVerificationIssue || errors.integrationIssue || errors.fatalError,
              uiTexts.error
            ));
          });
        }
        if (currentActiveOrganization && Platforms.checkIsNative()) {
          const { domainName } = currentActiveOrganization.whiteLabel || {};

          if (!domainName || domainName === window.location.host) {
            try {
              await SplashScreen.hide();
            } catch (error) {}
          }
        }
        if (onAppInitComplete) onAppInitComplete();
      } catch (errorA) {
        if (Platforms.checkIsNative()) {
          try {
            await SplashScreen.hide();
          } catch (errorB) {}
        }
        if (Environment.checkIsDevMode()) console.error(errorA); //eslint-disable-line
        setMaintenanceShown(true);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initialDataLoaded || !userData.id || !userData.email || !userData.role
      || !activeOrganization?.id || !activeOrganization?.name) return;

    const { businessId } = parseCurrentPath(pathname);

    if (businessId && (selectedBusinessId !== businessId
      || selectedBusinessData.id !== businessId || !selectedBusinessData.name)) {
      OpenReplay.stop();

      return;
    }
    OpenReplay.start({
      userId: userData.id,
      userEmail: userData.email,
      userType: UserRoles.getRoleType(userData.role),
      organizationId: activeOrganization.id,
      organizationName: activeOrganization?.name,
      businessId,
      businessName: businessId ? selectedBusinessData.name : "",
      rpaBusiness: businessId && selectedBusinessRpaMode ? "true" : "false"
    });
  }, [
    initialDataLoaded,
    userData.id,
    userData.email,
    userData.role,
    activeOrganization?.id,
    activeOrganization?.name,
    pathname,
    selectedBusinessId,
    selectedBusinessData.id,
    selectedBusinessData.name,
    selectedBusinessRpaMode
  ]);
  useEffect(() => {
    if (!initialDataLoaded || !paramsBusinessId || !selectedBusinessIdValid || !(activeOrganization || {}).countryCode) return;
    selectBusiness(paramsBusinessId);
  }, [activeOrganization, initialDataLoaded, paramsBusinessId, selectBusiness, selectedBusinessIdValid]);
  useEffect(() => {
    locationHistoryRef.current.push({ pathname, search });
  }, [pathname, search]);
  useEffect(() => {
    if (activeOrganization?.countryCode === Countries.CZ && selectedBusinessId) {
      dispatch(VatClassificationActions.fetch(true));
      dispatch(PreAccountingActions.fetch(true));
    }
  }, [activeOrganization?.countryCode, selectedBusinessId, dispatch]);
  useEffect(() => {
    if (!czCountry) return () => {};
    dispatch(InstitutionsActions.fetchLinksExpirationInfo());

    return Async.setInterval(() => {
      dispatch(InstitutionsActions.fetchLinksExpirationInfo());
    }, Constants.INSTITUTION_LINKS_EXPIRATION_UPDATE_INTERVAL);
  }, [czCountry, dispatch]);

  return (
    <LocationHistoryContext.Provider value={locationHistoryRef}>
      {maintenanceShown
        ? <MaintenanceHandler />
        : (preloaderShown
          ? (
            <>
              {modalWindowConfig ? <ModalContainer /> : <Preloader fixed />}
            </>
          )
          : (
            <Suspense fallback={<Preloader fixed />}>
              {oldUi
                ? <OldApp />
                : (Platforms.checkIsTouchDevice() ? <MobileApp /> : <NewApp />)}
            </Suspense>
          ))}
      <ToastContainer
        autoClose={Constants.NOTIFICATION_AUTO_CLOSE_DELAY}
        className={Css.toastContainer}
        toastClassName={Css.toast} />
      {preloaderOverlayShown && <Preloader className={Css.overlayPreloader} fixed />}
    </LocationHistoryContext.Provider>
  );
};

export default React.memo(App);
